export interface TimezoneClock {
	fullName: string;
	offset: number;
	cities: string[];
	about: string;
}

export const TZ_CLOCKS: Record<string, TimezoneClock> = {
	acst: {
		fullName: 'Australian Central Standard Time',
		offset: 9.5,
		cities: ['Adelaide', 'Darwin', 'Alice Springs'],
		about:
			'Australian Central Standard Time (ACST) is UTC+9:30, used in South Australia and the Northern Territory. In summer South Australia switches to Australian Central Daylight Time (ACDT, UTC+10:30), while the Northern Territory stays on ACST year-round. This clock always shows ACST (UTC+9:30).'
	},
	aedt: {
		fullName: 'Australian Eastern Daylight Time',
		offset: 11,
		cities: ['Sydney', 'Melbourne', 'Canberra', 'Hobart'],
		about:
			'Australian Eastern Daylight Time (AEDT) is UTC+11, the summer time of Australia’s eastern states. In winter the region returns to Australian Eastern Standard Time (AEST, UTC+10). This clock always shows AEDT (UTC+11).'
	},
	aest: {
		fullName: 'Australian Eastern Standard Time',
		offset: 10,
		cities: ['Sydney', 'Melbourne', 'Brisbane', 'Canberra'],
		about:
			'Australian Eastern Standard Time (AEST) is UTC+10, used along Australia’s east coast. In summer the southern states (but not Queensland) switch to AEDT (UTC+11). This clock always shows AEST (UTC+10).'
	},
	akdt: {
		fullName: 'Alaska Daylight Time',
		offset: -8,
		cities: ['Anchorage', 'Juneau', 'Fairbanks'],
		about:
			'Alaska Daylight Time (AKDT) is UTC-8, the summer time observed across most of Alaska. In winter the state returns to Alaska Standard Time (AKST, UTC-9). This clock always shows AKDT (UTC-8).'
	},
	akst: {
		fullName: 'Alaska Standard Time',
		offset: -9,
		cities: ['Anchorage', 'Juneau', 'Fairbanks'],
		about:
			'Alaska Standard Time (AKST) is UTC-9, used across most of Alaska in winter. In summer the state switches to Alaska Daylight Time (AKDT, UTC-8). This clock always shows AKST (UTC-9).'
	},
	ast: {
		fullName: 'Atlantic Standard Time',
		offset: -4,
		cities: ['Halifax', 'Saint John', 'Charlottetown'],
		about:
			'Atlantic Standard Time (AST) is UTC-4, used in Canada’s Atlantic provinces during winter. In summer the region observes Atlantic Daylight Time (ADT, UTC-3). This clock always shows AST (UTC-4).'
	},
	awst: {
		fullName: 'Australian Western Standard Time',
		offset: 8,
		cities: ['Perth', 'Bunbury', 'Geraldton'],
		about:
			'Australian Western Standard Time (AWST) is UTC+8, used in Western Australia. Western Australia does not observe daylight saving time, so AWST applies year-round. This clock always shows AWST (UTC+8).'
	},
	brt: {
		fullName: 'Brasília Time',
		offset: -3,
		cities: ['São Paulo', 'Rio de Janeiro', 'Brasília'],
		about:
			'Brasília Time (BRT) is UTC-3, the main time zone of Brazil covering its populous southeast. Brazil ended daylight saving time in 2019, so BRT applies year-round. This clock always shows BRT (UTC-3).'
	},
	bst: {
		fullName: 'British Summer Time',
		offset: 1,
		cities: ['London', 'Manchester', 'Edinburgh', 'Cardiff'],
		about:
			'British Summer Time (BST) is UTC+1, observed in the United Kingdom during summer. In winter the UK returns to Greenwich Mean Time (GMT, UTC+0). This clock always shows BST (UTC+1).'
	},
	cdt: {
		fullName: 'Central Daylight Time',
		offset: -5,
		cities: ['Chicago', 'Dallas', 'Houston', 'New Orleans'],
		about:
			'Central Daylight Time (CDT) is UTC-5, the summer time of the central United States. In winter the region returns to Central Standard Time (CST, UTC-6). This clock always shows CDT (UTC-5).'
	},
	cest: {
		fullName: 'Central European Summer Time',
		offset: 2,
		cities: ['Paris', 'Berlin', 'Madrid', 'Rome'],
		about:
			'Central European Summer Time (CEST) is UTC+2, observed across much of Europe in summer. In winter the region returns to Central European Time (CET, UTC+1). This clock always shows CEST (UTC+2).'
	},
	cet: {
		fullName: 'Central European Time',
		offset: 1,
		cities: ['Paris', 'Berlin', 'Madrid', 'Rome'],
		about:
			'Central European Time (CET) is UTC+1, used across much of continental Europe in winter. In summer the region switches to Central European Summer Time (CEST, UTC+2). This clock always shows CET (UTC+1).'
	},
	cst: {
		fullName: 'Central Standard Time',
		offset: -6,
		cities: ['Chicago', 'Dallas', 'Houston', 'New Orleans'],
		about:
			'Central Standard Time (CST) is UTC-6, used in the central United States during winter. In summer the region observes Central Daylight Time (CDT, UTC-5). This clock always shows CST (UTC-6). Note: the abbreviation CST is also used for China Standard Time (UTC+8); this page refers to US Central Time.'
	},
	edt: {
		fullName: 'Eastern Daylight Time',
		offset: -4,
		cities: ['New York', 'Toronto', 'Atlanta', 'Miami'],
		about:
			'Eastern Daylight Time (EDT) is UTC-4, the summer time of the eastern United States and Canada. In winter the region returns to Eastern Standard Time (EST, UTC-5). This clock always shows EDT (UTC-4).'
	},
	eest: {
		fullName: 'Eastern European Summer Time',
		offset: 3,
		cities: ['Athens', 'Helsinki', 'Bucharest', 'Kyiv'],
		about:
			'Eastern European Summer Time (EEST) is UTC+3, observed in eastern Europe during summer. In winter the region returns to Eastern European Time (EET, UTC+2). This clock always shows EEST (UTC+3).'
	},
	eet: {
		fullName: 'Eastern European Time',
		offset: 2,
		cities: ['Athens', 'Helsinki', 'Bucharest', 'Cairo'],
		about:
			'Eastern European Time (EET) is UTC+2, used across eastern Europe and parts of North Africa. Many EET regions switch to Eastern European Summer Time (EEST, UTC+3) in summer. This clock always shows EET (UTC+2).'
	},
	est: {
		fullName: 'Eastern Standard Time',
		offset: -5,
		cities: ['New York', 'Toronto', 'Atlanta', 'Miami'],
		about:
			'Eastern Standard Time (EST) is UTC-5, used along the east coast of North America during winter. In summer the region observes Eastern Daylight Time (EDT, UTC-4). This clock always shows EST (UTC-5).'
	},
	gmt: {
		fullName: 'Greenwich Mean Time',
		offset: 0,
		cities: ['London', 'Lisbon', 'Accra', 'Reykjavík'],
		about:
			'Greenwich Mean Time (GMT) is UTC+0, the global reference meridian. Parts of the UK and western Europe switch to summer time (UTC+1) in summer, but GMT itself is a constant offset. This clock always shows GMT (UTC+0).'
	},
	gst: {
		fullName: 'Gulf Standard Time',
		offset: 4,
		cities: ['Dubai', 'Abu Dhabi', 'Muscat'],
		about:
			'Gulf Standard Time (GST) is UTC+4, used in the United Arab Emirates and Oman. The Gulf region does not observe daylight saving time, so GST applies year-round. This clock always shows GST (UTC+4).'
	},
	hkt: {
		fullName: 'Hong Kong Time',
		offset: 8,
		cities: ['Hong Kong', 'Kowloon'],
		about:
			'Hong Kong Time (HKT) is UTC+8. Hong Kong does not observe daylight saving time, so HKT applies year-round. This clock always shows HKT (UTC+8).'
	},
	hst: {
		fullName: 'Hawaii Standard Time',
		offset: -10,
		cities: ['Honolulu', 'Hilo', 'Kailua'],
		about:
			'Hawaii Standard Time (HST) is UTC-10, used across the Hawaiian Islands. Hawaii does not observe daylight saving time, so HST applies year-round. This clock always shows HST (UTC-10).'
	},
	idt: {
		fullName: 'Israel Daylight Time',
		offset: 3,
		cities: ['Jerusalem', 'Tel Aviv', 'Haifa'],
		about:
			'Israel Daylight Time (IDT) is UTC+3, observed in Israel during summer. In winter Israel returns to Israel Standard Time (IST, UTC+2). This clock always shows IDT (UTC+3).'
	},
	ist: {
		fullName: 'India Standard Time',
		offset: 5.5,
		cities: ['Mumbai', 'Delhi', 'Bengaluru', 'Kolkata'],
		about:
			'India Standard Time (IST) is UTC+5:30, used across all of India and Sri Lanka. India does not observe daylight saving time, so IST applies year-round. This clock always shows IST (UTC+5:30).'
	},
	jst: {
		fullName: 'Japan Standard Time',
		offset: 9,
		cities: ['Tokyo', 'Osaka', 'Yokohama', 'Sapporo'],
		about:
			'Japan Standard Time (JST) is UTC+9, used throughout Japan. Japan does not observe daylight saving time, so JST applies year-round. This clock always shows JST (UTC+9).'
	},
	kst: {
		fullName: 'Korea Standard Time',
		offset: 9,
		cities: ['Seoul', 'Busan', 'Incheon'],
		about:
			'Korea Standard Time (KST) is UTC+9, used throughout South Korea. South Korea does not observe daylight saving time, so KST applies year-round. This clock always shows KST (UTC+9).'
	},
	mdt: {
		fullName: 'Mountain Daylight Time',
		offset: -6,
		cities: ['Denver', 'Salt Lake City', 'Albuquerque'],
		about:
			'Mountain Daylight Time (MDT) is UTC-6, the summer (daylight saving) time observed in the Mountain Time Zone of North America. In winter the region switches to Mountain Standard Time (MST, UTC-7). This clock always shows MDT (UTC-6).'
	},
	msk: {
		fullName: 'Moscow Standard Time',
		offset: 3,
		cities: ['Moscow', 'Saint Petersburg', 'Nizhny Novgorod'],
		about:
			'Moscow Standard Time (MSK) is UTC+3, used across western Russia including Moscow. Russia does not observe daylight saving time, so MSK applies year-round. This clock always shows MSK (UTC+3).'
	},
	mst: {
		fullName: 'Mountain Standard Time',
		offset: -7,
		cities: ['Phoenix', 'Denver', 'Salt Lake City'],
		about:
			'Mountain Standard Time (MST) is UTC-7, used in the Mountain Time Zone during winter. Most of the region switches to Mountain Daylight Time (MDT, UTC-6) in summer, though Arizona stays on MST year-round. This clock always shows MST (UTC-7).'
	},
	nzdt: {
		fullName: 'New Zealand Daylight Time',
		offset: 13,
		cities: ['Auckland', 'Wellington', 'Christchurch'],
		about:
			'New Zealand Daylight Time (NZDT) is UTC+13, observed in New Zealand during the southern-hemisphere summer. In winter the country returns to New Zealand Standard Time (NZST, UTC+12). This clock always shows NZDT (UTC+13).'
	},
	nzst: {
		fullName: 'New Zealand Standard Time',
		offset: 12,
		cities: ['Auckland', 'Wellington', 'Christchurch'],
		about:
			'New Zealand Standard Time (NZST) is UTC+12, used in New Zealand during winter. In summer the country switches to New Zealand Daylight Time (NZDT, UTC+13). This clock always shows NZST (UTC+12).'
	},
	pdt: {
		fullName: 'Pacific Daylight Time',
		offset: -7,
		cities: ['Los Angeles', 'San Francisco', 'Seattle', 'Vancouver'],
		about:
			'Pacific Daylight Time (PDT) is UTC-7, the summer time of the North American west coast. In winter the region returns to Pacific Standard Time (PST, UTC-8). This clock always shows PDT (UTC-7).'
	},
	pst: {
		fullName: 'Pacific Standard Time',
		offset: -8,
		cities: ['Los Angeles', 'San Francisco', 'Seattle', 'Las Vegas'],
		about:
			'Pacific Standard Time (PST) is UTC-8, used along the west coast of North America during winter. In summer the region observes Pacific Daylight Time (PDT, UTC-7). This clock always shows PST (UTC-8).'
	},
	sgt: {
		fullName: 'Singapore Standard Time',
		offset: 8,
		cities: ['Singapore'],
		about:
			'Singapore Standard Time (SGT) is UTC+8. Singapore does not observe daylight saving time, so SGT applies year-round. This clock always shows SGT (UTC+8).'
	},
	sst: {
		fullName: 'Samoa Standard Time',
		offset: -11,
		cities: ['Pago Pago', 'Midway Atoll'],
		about:
			'Samoa Standard Time (SST) is UTC-11, used in American Samoa and nearby Pacific islands. The region does not observe daylight saving time, so SST applies year-round. This clock always shows SST (UTC-11).'
	},
	utc: {
		fullName: 'Coordinated Universal Time',
		offset: 0,
		cities: ['Worldwide reference for servers, aviation, and APIs'],
		about:
			'Coordinated Universal Time (UTC) is the global time standard at UTC±0, used for servers, aviation, logs, and international coordination. UTC never observes daylight saving time. This clock always shows UTC (UTC+0).'
	}
};
